<div class="settings-container page-shell">
  <mat-toolbar class="header">
    <span>{{ "menu.advanced" | translate }}</span>
    <div class="header-buttons">
      <button
        mat-raised-button
        color="primary"
        type="button"
        (click)="onSubmit()"
        [disabled]="saving || !hasChanges || loading"
      >
        <mat-spinner *ngIf="saving" diameter="20" class="dc-btn-spinner"></mat-spinner>
        <mat-icon *ngIf="!saving" class="btn-icon">save</mat-icon>
        <span>{{ saving ? ("saving" | translate) : ("save" | translate) }}</span>
      </button>
    </div>
  </mat-toolbar>

  <form [formGroup]="form" class="form" (ngSubmit)="onSubmit()">
    <section class="form-section">
      <h3 class="section-title">
        {{ "settings.syncAndUpdateSettings" | translate }}
      </h3>

      <div class="option-list">
        <mat-checkbox formControlName="overwritingDataBySync" color="primary">
          {{ "settings.overwritingDataBySync" | translate }}
        </mat-checkbox>

        <div
          class="option-list option-list--nested"
          *ngIf="form.get('overwritingDataBySync')?.value"
        >
          <mat-checkbox
            formControlName="overwritingDataBySyncAndRemove"
            color="primary"
          >
            {{ "settings.overwritingDataBySyncAndRemove" | translate }}
          </mat-checkbox>
          <mat-checkbox formControlName="overwritingPhotoBySync" color="primary">
            {{ "settings.overwritingPhotoBySync" | translate }}
          </mat-checkbox>
        </div>

        <mat-checkbox
          formControlName="updateSignatureWhenGoogleDataChange"
          color="primary"
        >
          {{ "settings.updateSignatureWhenGoogleDataChange" | translate }}
        </mat-checkbox>

        <mat-checkbox
          formControlName="dailySignatureReinstallEnabled"
          color="primary"
        >
          {{ "settings.dailySignatureReinstallEnabled" | translate }}
        </mat-checkbox>

        <div
          class="option-list option-list--nested"
          *ngIf="form.get('dailySignatureReinstallEnabled')?.value"
        >
          <app-time-picker
            [label]="'settings.signatureReinstallTime' | translate"
            formControlName="signatureReinstallTime"
          ></app-time-picker>
        </div>
      </div>
    </section>

    <section class="form-section">
      <h3 class="section-title">
        {{ "settings.signatureLabelsSection" | translate }}
      </h3>

      <div class="field-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ "settings.phoneLabel" | translate }}</mat-label>
          <input matInput formControlName="phoneLabel" type="text" />
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ "settings.mobileLabel" | translate }}</mat-label>
          <input matInput formControlName="mobileLabel" type="text" />
        </mat-form-field>
      </div>

      <div class="field-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ "settings.emailLabel" | translate }}</mat-label>
          <input matInput formControlName="emailLabel" type="text" />
        </mat-form-field>

        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ "settings.customSignatureLabel" | translate }}</mat-label>
          <input matInput formControlName="customLabel" type="text" />
        </mat-form-field>
      </div>

      <app-searchable-select
        class="field-half"
        formControlName="customLabelVariable"
        [label]="'settings.customLabelVariable' | translate"
        [options]="customLabelVariableOptions"
        [displayWith]="identityLabel"
        [valueWith]="identityLabel"
        [emptyLabel]="'settings.customLabelVariableNone' | translate"
        [emptyValue]="null"
      ></app-searchable-select>

      <div class="field-row">
        <div class="color-field">
          <span class="color-field__label">{{
            "settings.labelColor" | translate
          }}</span>
          <div class="color-field__controls">
            <input
              type="color"
              [value]="form.get('labelColor')?.value || '#f0f0f0'"
              (input)="onLabelColorChange($event)"
              class="color-input"
            />
            <input
              type="text"
              class="color-hex-input"
              [value]="form.get('labelColor')?.value || ''"
              (blur)="onLabelColorHexBlur($event)"
              (keyup.enter)="onLabelColorHexBlur($event)"
              placeholder="#f0f0f0"
            />
          </div>
        </div>

        <div class="color-field">
          <span class="color-field__label">{{
            "settings.labelTextColor" | translate
          }}</span>
          <div class="color-field__controls">
            <input
              type="color"
              [value]="form.get('labelTextColor')?.value || '#333333'"
              (input)="onLabelTextColorChange($event)"
              class="color-input"
            />
            <input
              type="text"
              class="color-hex-input"
              [value]="form.get('labelTextColor')?.value || ''"
              (blur)="onLabelTextColorHexBlur($event)"
              (keyup.enter)="onLabelTextColorHexBlur($event)"
              placeholder="#333333"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="form-section">
      <h3 class="section-title">
        {{ "settings.defaultsSection" | translate }}
      </h3>

      <div class="field-row">
        <app-searchable-select
          *ngIf="digitalCardEnabled"
          formControlName="digitalCardProfileId"
          [label]="'settings.defaultDigitalCardTemplateForQr' | translate"
          [options]="digitalCardTemplates"
          [displayWith]="digitalCardTemplateLabel"
          [valueWith]="digitalCardTemplateValue"
          [emptyLabel]="'settings.defaultDigitalCardTemplateNone' | translate"
          [emptyValue]="null"
          [loading]="digitalCardTemplatesLoading"
          (opened)="loadDigitalCardTemplates()"
        ></app-searchable-select>

        <app-searchable-select
          formControlName="defaultCountryCallingCode"
          [label]="'settings.defaultCountryCallingCode' | translate"
          [options]="countries"
          [displayWith]="callingCodeLabel"
          [valueWith]="callingCodeValue"
          [searchWith]="callingCodeSearch"
        ></app-searchable-select>
      </div>

      <div class="field-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ "settings.domainLanguage" | translate }}</mat-label>
          <mat-select formControlName="language">
            <mat-option value="en">EN</mat-option>
            <mat-option value="es">ES</mat-option>
            <mat-option value="pl">PL</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </section>

    <section class="form-section">
      <h3 class="section-title">{{ "settings.userSettings" | translate }}</h3>

      <div class="option-grid">
        <mat-checkbox
          formControlName="confirmationOfSignatureInstallationByUser"
          color="primary"
        >
          {{ "settings.confirmationOfSignatureInstallationByUser" | translate }}
        </mat-checkbox>
        <mat-checkbox formControlName="bannerDisablingByUser" color="primary">
          {{ "settings.bannerDisablingByUser" | translate }}
        </mat-checkbox>
        <mat-checkbox
          formControlName="firstInstallationEmailEnabled"
          color="primary"
        >
          {{ "settings.firstInstallationEmailEnabled" | translate }}
        </mat-checkbox>
        <mat-checkbox formControlName="avatarEnabled" color="primary">
          {{ "settings.avatarEnabled" | translate }}
        </mat-checkbox>
        <mat-checkbox formControlName="linksEnabled" color="primary">
          {{ "settings.linksEnabled" | translate }}
        </mat-checkbox>
        <mat-checkbox formControlName="handwrittenEnabled" color="primary">
          {{ "settings.handwrittenEnabled" | translate }}
        </mat-checkbox>
        <mat-checkbox formControlName="badgesEnabled" color="primary">
          {{ "settings.badgesEnabled" | translate }}
        </mat-checkbox>
      </div>
    </section>
  </form>

  <div class="dbc1-loading dbc1-loading--overlay" *ngIf="loading">
    <app-loading></app-loading>
  </div>
</div>
